import { apSelfAssessmentProofSchema, } from '../schemas/ap-self-assessment.js';
import { addDecimal, compareDecimal, divideDecimal, multiplyDecimal } from './decimal.js';
import { calculateVendorInvoiceAccountingAllocation } from './vendor-invoice-accounting.js';
const SCALE = 4;
const sum = (values) => values.reduce((total, value) => addDecimal(total, value, SCALE), '0.0000');
export const AP_SELF_ASSESSMENT_LEGAL_BASES = {
    IMPORT_GOODS_NO_RECIPIENT_VAT: 'UStG §1(1)4; separate customs evidence; no §1a/§13b supplier VAT',
    FOREIGN_LOCAL_VAT_NO_RECIPIENT_VAT: 'Foreign local supply; no German §1a/§13b VAT; no §15 German input VAT',
    EU_ACQUISITION: 'UStG §1a; §13(1)6; §15(1)3',
    EU_SERVICE: 'UStG §3a(2); §13b(1),(5),(7); §15(1)4',
    RC_FOREIGN_WORK_SERVICE: 'UStG §13b(2)1,(5),(6),(7); §15(1)4',
    RC_SECURITY: 'UStG §13b(2)2,(5); §15(1)4',
    RC_PROPERTY: 'UStG §13b(2)3,(5); §15(1)4',
    RC_CONSTRUCTION: 'UStG §13b(2)4,(5); §15(1)4',
    RC_FOREIGN_ENERGY: 'UStG §13b(2)5a,(5),(7); §15(1)4',
    RC_GAS: 'UStG §13b(2)5b,(5); §15(1)4',
    RC_ELECTRICITY: 'UStG §13b(2)5b,(5); §15(1)4',
    RC_EMISSIONS: 'UStG §13b(2)6,(5); §15(1)4',
    RC_SCRAP: 'UStG §13b(2)7,(5); §15(1)4',
    RC_CLEANING: 'UStG §13b(2)8,(5); §15(1)4',
    RC_GOLD: 'UStG §13b(2)9,(5); §15(1)4',
    RC_ELECTRONICS: 'UStG §13b(2)10,(5); §15(1)4',
    RC_METALS: 'UStG §13b(2)11,(5); §15(1)4',
    RC_TELECOM: 'UStG §13b(2)12,(5); §15(1)4',
};
export function apSelfAssessmentIsNonRecipientCase(value) {
    return ['IMPORT_GOODS_NO_RECIPIENT_VAT', 'FOREIGN_LOCAL_VAT_NO_RECIPIENT_VAT'].includes(value);
}
/** Dates identify the statutory return period; late receipt never delays recipient VAT. */
export function apSelfAssessmentTaxDate(kind, invoiceDate, serviceDate, receivedDate) {
    if (apSelfAssessmentIsNonRecipientCase(kind))
        return [invoiceDate, serviceDate, receivedDate ?? invoiceDate].sort().at(-1) ?? invoiceDate;
    if (kind === 'EU_SERVICE')
        return serviceDate;
    const service = new Date(`${serviceDate}T00:00:00Z`);
    const latest = new Date(Date.UTC(service.getUTCFullYear(), service.getUTCMonth() + 2, 0))
        .toISOString()
        .slice(0, 10);
    return invoiceDate < serviceDate ? serviceDate : invoiceDate < latest ? invoiceDate : latest;
}
/** The source payable remains unchanged; synthetic statutory tax is never supplier VAT. */
export function calculateApSelfAssessment(input) {
    const { source, review } = input;
    if (!source.eligible ||
        source.grossAmount === null ||
        source.taxAmount === null ||
        source.homeCostAmount === null)
        throw new Error('apSelfAssessment.invalid');
    if (apSelfAssessmentIsNonRecipientCase(review.case)) {
        if (review.allocations.length ||
            source.rows.some((row) => compareDecimal(row.inputTaxDeductionPercent, '0') !== 0) ||
            (review.case === 'IMPORT_GOODS_NO_RECIPIENT_VAT' &&
                compareDecimal(source.taxAmount, '0') !== 0))
            throw new Error('apSelfAssessment.invalid');
        return apSelfAssessmentProofSchema.parse({
            revision: input.revision,
            review,
            taxDate: apSelfAssessmentTaxDate(review.case, input.invoiceDate, input.serviceDate, input.receivedDate),
            legalBasis: AP_SELF_ASSESSMENT_LEGAL_BASES[review.case],
            originalTaxAmount: source.taxAmount,
            payableAmount: source.grossAmount,
            legalTaxAmount: '0.0000',
            deductibleTaxAmount: '0.0000',
            costAmount: source.homeCostAmount,
            rows: [],
        });
    }
    const amounts = review.allocations.map((allocation) => {
        const row = source.rows.find((part) => part.position === allocation.position);
        if (!row ||
            compareDecimal(row.sourceTaxAmount, '0') !== 0 ||
            compareDecimal(row.taxRate, '0') !== 0)
            throw new Error('apSelfAssessment.invalid');
        const index = review.allocations.indexOf(allocation);
        const fiscal = input.fiscalFx?.amounts[index];
        if (input.fiscalFx &&
            (!fiscal ||
                fiscal.originalNet !== row.netAmount ||
                fiscal.originalTax !== row.sourceTaxAmount ||
                fiscal.taxRatePct !== allocation.rate))
            throw new Error('apSelfAssessment.invalid');
        return { ...row, netAmount: fiscal?.fiscalNet ?? row.netAmount, taxRate: allocation.rate };
    });
    const groups = new Map();
    for (const row of amounts)
        groups.set(row.taxRate, addDecimal(groups.get(row.taxRate) ?? '0', row.netAmount, SCALE));
    const tax = calculateVendorInvoiceAccountingAllocation({
        buckets: [...groups].map(([taxRate, netAmount]) => ({
            taxRate,
            netAmount,
            taxAmount: divideDecimal(multiplyDecimal(netAmount, taxRate, 10), '100', SCALE),
        })),
        splits: amounts,
        exchangeRate: '1',
    });
    if (!tax.eligible)
        throw new Error('apSelfAssessment.invalid');
    const rows = tax.rows.map((row) => {
        const original = source.rows.find((part) => part.position === row.position);
        if (!original)
            throw new Error('apSelfAssessment.invalid');
        const nondeductible = addDecimal(row.costAmount, multiplyDecimal(row.netAmount, '-1', SCALE), SCALE);
        return {
            position: row.position,
            rate: row.taxRate,
            legalTaxAmount: row.sourceTaxAmount,
            deductibleTaxAmount: row.deductibleTaxAmount,
            nondeductibleTaxAmount: addDecimal(row.costAmount, multiplyDecimal(row.netAmount, '-1', SCALE), SCALE),
            costAmount: input.fiscalFx
                ? addDecimal(original.homeCostAmount, nondeductible, SCALE)
                : row.costAmount,
            ...(input.fiscalFx
                ? {
                    originalNetAmount: original.netAmount,
                    fiscalNetAmount: row.netAmount,
                    bookCostAmount: original.homeCostAmount,
                }
                : {}),
        };
    });
    return apSelfAssessmentProofSchema.parse({
        ...(input.fiscalFx
            ? {
                sourceMaterial: input.sourceMaterial,
                fiscalFx: input.fiscalFx,
                sourceCurrency: input.sourceCurrency,
                bookExchangeRate: input.bookExchangeRate,
            }
            : {}),
        revision: input.revision,
        review,
        taxDate: apSelfAssessmentTaxDate(review.case, input.invoiceDate, input.serviceDate),
        legalBasis: AP_SELF_ASSESSMENT_LEGAL_BASES[review.case],
        originalTaxAmount: source.taxAmount,
        payableAmount: source.grossAmount,
        legalTaxAmount: sum(rows.map((row) => row.legalTaxAmount)),
        deductibleTaxAmount: sum(rows.map((row) => row.deductibleTaxAmount)),
        costAmount: addDecimal(source.homeCostAmount, sum(rows.map((row) => row.nondeductibleTaxAmount)), SCALE),
        rows,
    });
}
//# sourceMappingURL=ap-self-assessment.js.map